/**
 * @file sale-price-visibility.ts
 * @description Видит ли сотрудник цену продажи (phase19.31; контракт
 * cabinet-api-contract §11, W11/SC51; зеркало серверного
 * `apps/api/src/rbac/sale-price-visibility.ts`).
 *
 * Правило через права, а не через код роли (решение пользователя 2026-10-02
 * «цен продажи не видит только кладовщик»): цену НЕ видит контекст магазина,
 * у которого нет ни одного права магазина, кроме складских (`SHOP_WMS_*`).
 * Контекст платформы и любая роль хотя бы с одним нескладским правом
 * магазина — видят. Прежний признак «экран кладовщика» (`isWarehouseMode`)
 * смотрел на код роли и расходился с сервером на частных наборах прав.
 *
 * Закупочная цена (`unitCost`) сюда не относится: кладовщик её видит и вносит.
 */

import type { AccountContext } from "../auth/types";

/** Префикс складских прав: только они цен продажи не открывают. */
const WAREHOUSE_PERMISSION_PREFIX = "SHOP_WMS_";

/** Право магазина, которое открывает цены продажи (любое нескладское). */
function grantsSalePrices(permission: string): boolean {
  return permission.startsWith("SHOP_") && !permission.startsWith(WAREHOUSE_PERMISSION_PREFIX);
}

/**
 * Показывать ли цену продажи. Нет активного контекста — не показываем
 * (сервер такому запросу цен не отдаст).
 */
export function canSeeSalePrice(ctx: Pick<AccountContext, "contextType" | "role"> | null | undefined): boolean {
  if (!ctx) return false;
  if (ctx.contextType === "PLATFORM") return true;
  return ctx.role.permissions.some(grantsSalePrices);
}
